import { xor } from 'lodash';
import { interpolate } from '@format/interpolate';
import { getVariantsSortedByPrice } from '../../products';
import { ApiResult } from '../../api';
import { MEAL_PREP_TYPES } from '../constants';
import {
  ACTIONS,
  ACTION_TYPES,
  STATE,
  pathToAction,
  ALL_RECIPES,
} from './constants';

export const getLowestCostVariant = starterBoxVariants => {
  const variants = getVariantsSortedByPrice(starterBoxVariants);
  return variants[0];
};

export const getRecipeCombination = (recipeCombinations, recipes) => {
  const variant = recipeCombinations.find(
    variant =>
      !xor(variant.recipes.fresh, recipes.fresh).length &&
      !xor(variant.recipes.baked, recipes.baked).length,
  );
  return variant;
};

export const saveCart = async (ctx, payload) => {
  const {
    reporter,
    combinationId,
    setShowModal,
    setLocalSelection,
    cartProduct,
    petId,
    cartApi,
    defaultCadence,
    subscriptionPrice,
  } = payload.props;
  // If the same meal plan is already in cart, no need to update
  const lineItemForPet = cartProduct;
  if (combinationId === lineItemForPet?.combination_id) {
    return ApiResult.success();
  }
  const selectedCadence = payload.props?.selectedCadence;

  let result;
  result = await cartApi.upsertMealPlan(petId, {
    combination_id: combinationId,
    ...(selectedCadence ? { subscription_cadence: selectedCadence } : {}),
  });

  const petsInCart = result?.payload?.pets;
  const starterBox = petsInCart?.[0]?.starter_box;

  if (!!starterBox?.cadence && !!defaultCadence) {
    if (petsInCart?.length === 1) {
      cartApi.updateCartAttributes({
        starter_box_cadence: starterBox?.cadence,
        subscription_default_cadence: defaultCadence,
        subscription_price: subscriptionPrice,
        starter_box_price: starterBox?.price ?? null,
      });
    } else {
      //if a second pet is added to the cart we want to update the values
      cartApi.updateCartAttributes({
        starter_box_cadence: null,
        subscription_default_cadence: null,
        subscription_price: null,
        starter_box_price: null,
      });
    }
  }

  ApiResult.match(result, {
    success: () => setLocalSelection(null, { skipRender: true }),
    error: {
      client: error => {
        reporter.error(error.original);
        setShowModal(true);
      },
      server: () => setShowModal(true),
    },
  });

  return result;
};

export const onCartSubmission = service => {
  if (service.machine.current !== STATE.submitCart) {
    return Promise.resolve(ApiResult.success());
  }
  return new Promise(resolve => {
    const checkSuccess = setInterval(() => {
      if (service.machine.current !== STATE.submitCart) {
        clearInterval(checkSuccess);
        resolve(service.context.submitCartResult);
      }
    }, 500);
  });
};

/**
 * Accepts a pathname as a string and returns the corresponding stateKey
 * @param {string} pathname
 * @param {string} [rootPath='']
 * @returns {string}
 */
export const getKeyFromPath = (pathname, rootPath) => {
  return pathname.replace(rootPath, '').replace(/\/$/, ''); // remove rootpath, then remove any trailing '/'
};

export const getActionFromKey = (key, service) => {
  const action = pathToAction[key];

  const machine = service.machine.original ?? service.machine;
  const transitions = machine.states[service.context.stateKey].transitions;

  if (action && Array.from(transitions.keys()).includes(action({}).type))
    return action;

  for (let [transitionKey, values] of transitions) {
    if (transitionKey === ACTION_TYPES.goBack) {
      for (let value of values) {
        if (value.guards(service.context, {})) return ACTIONS.goBack;
      }
    }
  }

  return action;
};

export const goBackReducer = (ctx, { payload }) => {
  const newCtx = {
    ...ctx,
    initialFlow: false,
    pets: payload.pets ?? ctx.pets,
    pet: payload.pet ?? ctx.pet,
    pet_id: payload.pet_id ?? ctx.pet_id,
    cart: payload.cart ?? ctx.cart,
    customer: payload.customer ?? ctx.customer,
  };

  // If customer goes back a step, and we'd set to an empty pet id,
  // and the customer actually has a pet available,
  // grab the pet id from that existing pet and set it
  // as our pet_id.
  if (newCtx.pet_id === '' && newCtx.pets?.[0]?.id) {
    newCtx.pet_id = newCtx.pets?.[0]?.id;
  }
  return newCtx;
};

export const toInterstitialReducer = (ctx, { payload }) => ({
  ...ctx,
  stateKey: STATE.interstitial,
  fromQuestion: payload.fromQuestion,
  interstitial: payload.interstitial,
  pet: payload.pet ?? ctx.pet,
  pets: payload.pets ?? ctx.pets,
  pet_id: payload.pet?.id ?? ctx.pet_id,
  flags: { ...ctx.flags, ...(payload.flags || {}) },
});

export const getUnavailableRecipes = availableRecipes =>
  ALL_RECIPES.filter(recipe => !availableRecipes.includes(recipe));

export const getUnavailableMealPrepTypes = combinationsByPlanType =>
  MEAL_PREP_TYPES.filter(type => {
    const planType = combinationsByPlanType[type];
    return (
      !planType ||
      !planType.planCombinations ||
      planType.planCombinations.length === 0
    );
  });

export const getSelectedAllergies = (selectedAllergies, allergies) => {
  return allergies?.filter(allergy =>
    selectedAllergies?.some(sel => sel.name === allergy.name),
  );
};

export const normalize = allergies =>
  allergies?.map(allergy => ({
    label: allergy.name,
    value: allergy.id,
  }));

export const getBreedVariant = (items = [], breeds = []) => {
  if (!items?.length) return null;

  for (const breed of breeds ?? []) {
    const match = items.find(
      ({ breedVariation } = {}) =>
        breedVariation && breedVariation === breed?.id,
    );
    if (match) return match;
  }

  return items.find(({ breedVariation } = {}) => !breedVariation) ?? null;
};

export const formatMultipetText = (template, names) => {
  if (!template || names.length === 0) return undefined;
  const joined =
    names.length === 1
      ? names[0]
      : names.length === 2
      ? `${names[0]} and ${names[1]}`
      : `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`;

  const isPlural = names.length > 1;

  return interpolate(template, {
    pupName: joined,
    plan: isPlural ? 'plans' : 'plan',
    box: isPlural ? 'boxes' : 'box',
    verb: isPlural ? 'are' : 'is',
  });
};

export const getRecommendedGiftForPet = (giftType, petWeight, couponCode) => {
  if (!giftType) return null;

  const choices = giftType.choices ?? [];

  const weightBasedChoice =
    petWeight != null && !!giftType.hasWeightRecommendations
      ? choices.find(
          ({ minWeight, maxWeight }) =>
            minWeight != null &&
            maxWeight != null &&
            petWeight >= minWeight &&
            petWeight <= maxWeight,
        )
      : null;

  const promoBasedChoice =
    couponCode && !giftType.hasWeightRecommendations
      ? choices.find(({ discountCodes }) =>
          discountCodes.some(
            code => code.toLowerCase() === couponCode.toLowerCase(),
          ),
        )
      : null;

  const recommendedChoice =
    (weightBasedChoice?.disabled ? null : weightBasedChoice) ??
    (promoBasedChoice?.disabled ? null : promoBasedChoice) ??
    giftType.defaultChoice?.[0] ??
    null;

  return recommendedChoice;
};
